// Sections 15 and 16: the festival, pairings awaiting the player's approval, and couples with
// their chance of a child (the `conception` target's explain() factors). Props only.
import type { JobId, PromptId } from '../../sim/api';
import type { CoupleRow, PairingRow, SocietyVM } from '../../viewModels/society';
import { seasonLabel, societyText } from '../text/en';
import { FactorTip } from './factor-tip';

interface FestivalProps {
  festival: SocietyVM['festival'];
}

export function FestivalPanel({ festival: f }: FestivalProps) {
  return (
    <div class="society-festival">
      {f.activeSeasons > 0 && <p>{societyText.festivalActive(f.activeSeasons)}</p>}
      {f.next === null ? (
        <p class="society-muted">{societyText.festivalOff}</p>
      ) : (
        <p>
          {societyText.festivalNext(seasonLabel[f.next])}.{' '}
          <span class="num">{societyText.festivalCost(f.food, f.wood)}</span>
        </p>
      )}
      {!f.hall && <p class="society-warn">{societyText.festivalNoHall}</p>}
      {f.last !== null && (
        <p class="society-muted">
          {societyText.festivalLast(societyText.sinceWhen(seasonLabel[f.last.season], f.last.year))}
        </p>
      )}
    </div>
  );
}

interface PairingProps {
  rows: readonly PairingRow[];
  canEdit: boolean;
  jobLabel: (job: JobId) => string;
  onAnswer: (promptId: PromptId, approve: boolean) => void;
}

export function PairingsPanel({ rows, canEdit, jobLabel, onAnswer }: PairingProps) {
  if (rows.length === 0) return <p class="society-muted">{societyText.pairingsEmpty}</p>;
  return (
    <ul class="society-list">
      {rows.map((r) => (
        <li key={r.promptId} class="society-pairing">
          <span>
            {r.people
              .map((p) => `${p.name} (${p.ageYears}, ${jobLabel(p.job).toLowerCase()})`)
              .join(' and ')}
          </span>{' '}
          <span class="society-actions" role="group" aria-label={societyText.answer}>
            <button
              type="button"
              class="button button--small"
              aria-pressed={r.answer === true}
              disabled={!canEdit}
              onClick={() => onAnswer(r.promptId, true)}
            >
              {societyText.approve}
            </button>
            <button
              type="button"
              class="button button--small"
              aria-pressed={r.answer === false}
              disabled={!canEdit}
              onClick={() => onAnswer(r.promptId, false)}
            >
              {societyText.refuse}
            </button>
          </span>
          {r.answer !== null && (
            <span class="society-muted">
              {' '}
              {r.answer ? societyText.approved : societyText.refused}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

interface CouplesProps {
  rows: readonly CoupleRow[];
}

export function CouplesPanel({ rows }: CouplesProps) {
  if (rows.length === 0) return <p class="society-muted">{societyText.couplesEmpty}</p>;
  return (
    <table class="society-table">
      <thead>
        <tr>
          <th scope="col">{societyText.colCouple}</th>
          <th scope="col">{societyText.colConception}</th>
          <th scope="col">{societyText.colExpecting}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((c) => (
          <tr key={c.motherId}>
            <th scope="row">
              {c.mother} and {c.father}
            </th>
            <td>
              <FactorTip
                value={`${Math.round(c.conception.value * 1000) / 10}%`}
                tooltip={c.conception}
                label={societyText.conceptionLabel(c.mother)}
              />
            </td>
            <td class="num">{c.pregnant === null ? '' : societyText.dueIn(c.pregnant)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
